import { isBefore } from 'date-fns'
import { useMemo, useState } from 'react'
import { TransitionLoader } from '@opallabs/kuiper'
import { useOnce } from '@opallabs/lambda'
import { TASK_GLYPH } from '@opallabs/lapidary'
import type { WithTheme } from '@opallabs/lapidary'
import { TaskItem } from '@opallabs/satellite'
import { AssignmentStatus } from '@opallabs/satellite/src/AssignmentHelpers/types'
import { NormalizedTask } from '@opallabs/wormhole/types'
import { CollapsibleList } from '../../CollapsibleList'
import { EmptyState } from '../../Shared/EmptyState'
import * as sharedStyles from '../../Shared/styles'
import { Runnable } from '../../types'

export type TodoPanelProps = Readonly<
  WithTheme & {
    createOverdueCheckpoint: Runnable
    createToDoCheckpoint: Runnable
    getOverdueTasks: Runnable
    getTodoTasks: Runnable
    isLoading: boolean
    onOverdueCheckboxClick: (task: NormalizedTask) => void
    onOverdueStarClick: (task: NormalizedTask) => void
    onTodoCheckboxClick: (task: NormalizedTask) => void
    onTodoStarClick: (task: NormalizedTask) => void
    overdueTasks: NormalizedTask[]
    setTaskPreviewCard: (task: NormalizedTask | null) => void
    todoTasks: NormalizedTask[]
  }
>

export const TodoPanel: React.FC<TodoPanelProps> = ({
  createOverdueCheckpoint,
  createToDoCheckpoint,
  getOverdueTasks,
  getTodoTasks,
  isLoading,
  onOverdueCheckboxClick,
  onOverdueStarClick,
  onTodoCheckboxClick,
  onTodoStarClick,
  overdueTasks,
  setTaskPreviewCard,
  theme = 'light',
  todoTasks,
}) => {
  const [activeTask, setActiveTask] = useState<string | number>(
    todoTasks.length ? todoTasks[0].id : -1
  )

  const isEmpty = useMemo(
    () => todoTasks.length === 0 && overdueTasks.length === 0,
    [todoTasks, overdueTasks]
  )

  useOnce(() => {
    getOverdueTasks()
    getTodoTasks()
  })

  const starredTasks = useMemo(() => {
    const tempTasks: Array<NormalizedTask> = []
    tempTasks.push(...todoTasks.filter((task) => task.starred))
    tempTasks.push(...overdueTasks.filter((task) => task.starred))
    return tempTasks
  }, [todoTasks, overdueTasks])

  if (!isLoading && isEmpty) {
    return (
      <EmptyState
        icon={TASK_GLYPH}
        text="You don’t have any tasks to do right now"
        theme={theme}
      />
    )
  }

  return (
    <TransitionLoader
      loaded={!isLoading}
      minHeight={600} // this is an arbitrary size that looks good
      theme="transparent"
    >
      <div>
        <CollapsibleList
          defaultExpanded={true}
          styleOverrides={{
            base: sharedStyles.collapsibleList,
            titleRow: sharedStyles.collapsibleListTitle({ theme }),
          }}
          title="Starred Tasks"
          iconName="gem-channel-star-filled"
          textRender={(count): string =>
            `You have ${count} starred task${count !== 1 ? 's' : ''}`
          }
        >
          {starredTasks.map((task) => (
            <TaskItem
              {...task}
              taskStatus={
                activeTask === task.id
                  ? AssignmentStatus.Active
                  : task.taskStatus
              }
              key={`task-starred-${task.id}`}
              overdue={
                task.dueDate !== undefined
                  ? isBefore(task.dueDate, new Date())
                  : false
              }
              onItemClick={(): void => {
                setActiveTask(task.id)
                setTaskPreviewCard(task)
              }}
              onCheckboxClick={(): void => onTodoCheckboxClick(task)}
              onStarClick={(): void => onTodoStarClick(task)}
            />
          ))}
        </CollapsibleList>
        <CollapsibleList
          defaultExpanded={true}
          onExpand={createOverdueCheckpoint}
          styleOverrides={{
            base: sharedStyles.collapsibleList,
            titleRow: sharedStyles.collapsibleListTitle({ theme }),
          }}
          warning={true}
          title="Overdue Tasks"
          iconName="gem-warning"
          textRender={(count): string =>
            `You have ${count} overdue task${count !== 1 ? 's' : ''}`
          }
        >
          {overdueTasks
            .filter((task) => !task.starred)
            .map((task) => (
              <TaskItem
                {...task}
                taskStatus={
                  activeTask === task.id
                    ? AssignmentStatus.Active
                    : task.taskStatus
                }
                overdue={true}
                key={`task-overdue-${task.id}`}
                onItemClick={(): void => {
                  setActiveTask(task.id)
                  setTaskPreviewCard(task)
                }}
                onCheckboxClick={(): void => onOverdueCheckboxClick(task)}
                onStarClick={(): void => onOverdueStarClick(task)}
              />
            ))}
        </CollapsibleList>
        <CollapsibleList
          defaultExpanded={true}
          onExpand={createToDoCheckpoint}
          styleOverrides={{
            base: sharedStyles.collapsibleList,
            titleRow: sharedStyles.collapsibleListTitle({ theme }),
          }}
          title="Tasks Assigned to Me"
          iconName={TASK_GLYPH}
          iconSystem="hypernova"
          textRender={(count): string =>
            `You have ${count} task${count !== 1 ? 's' : ''}`
          }
        >
          {todoTasks
            .filter((task) => !task.starred)
            .map((task) => (
              <TaskItem
                {...task}
                taskStatus={
                  activeTask === task.id
                    ? AssignmentStatus.Active
                    : task.taskStatus
                }
                key={`task-assigned-${task.id}`}
                onItemClick={(): void => {
                  setActiveTask(task.id)
                  setTaskPreviewCard(task)
                }}
                onCheckboxClick={(): void => onTodoCheckboxClick(task)}
                onStarClick={(): void => onTodoStarClick(task)}
              />
            ))}
        </CollapsibleList>
      </div>
    </TransitionLoader>
  )
}
